/**
 * Attachments are voided rather than deleted (migration 109). The row and the
 * file both stay, so a record still shows what was attached and why it was set
 * aside, and voiding can be undone.
 *
 * Every attachment table carries the same four columns, and every void endpoint
 * is a PATCH on the attachment's own URL, so the shape and the request live here
 * once and each API module just supplies the URL.
 */

/** The void columns as the API reports them, present on every attachment row. */
export interface AttachmentVoidFields {
  status: 'Active' | 'Void';
  void_reason: string | null;
  voided_at: string | null;
  voided_by_user_id: number | null;
  voided_by: string | null;
}

/** True when this attachment has been voided. Tolerates rows from an older response. */
export function isVoided(row: { status?: string | null } | null | undefined): boolean {
  return String(row?.status ?? '').toLowerCase() === 'void';
}

/** Splits a list into the files still in play and the ones that were voided. */
export function splitVoided<T extends { status?: string | null }>(rows: T[]): { active: T[]; voided: T[] } {
  const active: T[] = [];
  const voided: T[] = [];
  for (const row of rows) (isVoided(row) ? voided : active).push(row);
  return { active, voided };
}

/**
 * Voids the attachment at `url` when given a reason, or unvoids it when `reason`
 * is null. `userId` records who did it where the endpoint tracks that.
 */
export async function patchAttachmentVoid<T>(
  url: string,
  reason: string | null,
  userId?: number | null
): Promise<T> {
  const body: Record<string, unknown> =
    reason == null ? { unvoid: true } : { status: 'Void', void_reason: reason };
  if (userId != null && userId > 0) body.user_id = userId;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((data as { error?: string }).error || 'Failed to update the attachment.');
  }
  return data as T;
}
